<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white py-20 overflow-hidden">
  <!-- Background Image Overlay -->
  <div class="absolute inset-0 z-0">
    <img
      src="https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=1920&h=800&fit=crop"
      alt="Marketing Automation"
      class="w-full h-full object-cover opacity-20"
    />
    <div class="absolute inset-0 bg-gradient-to-br from-primary-800/90 via-primary-700/90 to-primary-600/90"></div>
  </div>

  <div class="container mx-auto px-6 relative z-10">
    <!-- Breadcrumb -->
    <nav class="flex items-center space-x-2 text-sm mb-8">
      <a routerLink="/" class="hover:text-primary-200 transition-colors">Home</a>
      <span class="text-primary-200">/</span>
      <a routerLink="/services" class="hover:text-primary-200 transition-colors">Services</a>
      <span class="text-primary-200">/</span>
      <a routerLink="/services/performance-marketing" class="hover:text-primary-200 transition-colors">Performance Marketing</a>
      <span class="text-primary-200">/</span>
      <span class="text-primary-200">Marketing Automation</span>
    </nav>

    <div class="max-w-4xl">
      <h1 class="text-5xl md:text-6xl font-bold mb-6 leading-tight">
        Marketing Automation Services
      </h1>
      <p class="text-xl md:text-2xl mb-8 text-primary-100 leading-relaxed">
        Transform your marketing operations with intelligent automation that nurtures leads, personalizes engagement, and drives revenue growth at scale.
      </p>
      <div class="flex flex-wrap gap-4">
        <a routerLink="/contact" class="bg-white text-primary-900 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all transform hover:scale-105 shadow-lg">
          Get Started
        </a>
        <a href="#case-study" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-900 transition-all">
          View Case Study
        </a>
      </div>
    </div>
  </div>

  <!-- Decorative Elements -->
  <div class="absolute top-20 right-10 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
  <div class="absolute bottom-20 left-10 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
</section>

<!-- Features Section -->
<section class="py-20 bg-white">
  <div class="container mx-auto px-6">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        Comprehensive Automation Solutions
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        From platform selection to advanced workflow optimization, we provide end-to-end marketing automation services that scale your growth.
      </p>
    </div>

    <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      <div *ngFor="let feature of features" class="group bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-200 hover:border-primary-300 hover:shadow-xl transition-all duration-300">
        <div class="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
          <span class="material-icons text-white text-3xl">{{ feature.icon }}</span>
        </div>
        <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ feature.title }}</h3>
        <p class="text-gray-600 leading-relaxed">{{ feature.description }}</p>
      </div>
    </div>
  </div>
</section>

<!-- Process Section -->
<section class="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
  <div class="container mx-auto px-6">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        Our Automation Process
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        A proven methodology that takes you from manual processes to automated marketing excellence.
      </p>
    </div>

    <div class="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
      <div *ngFor="let item of process" class="relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-l-4 border-primary-600">
        <div class="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
          {{ item.step }}
        </div>
        <div class="ml-8">
          <h3 class="text-2xl font-bold text-gray-900 mb-4">{{ item.title }}</h3>
          <p class="text-gray-600 leading-relaxed">{{ item.description }}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Case Study Section -->
<section id="case-study" class="py-20 bg-white">
  <div class="container mx-auto px-6">
    <div class="text-center mb-16">
      <h2 class="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        Success Story
      </h2>
      <p class="text-xl text-gray-600">
        How we helped {{ caseStudy.client }} automate their way to 3x conversion growth
      </p>
    </div>

    <div class="max-w-6xl mx-auto">
      <div class="grid md:grid-cols-2 gap-12 items-center mb-12">
        <div>
          <img
            [src]="caseStudy.image"
            [alt]="caseStudy.client"
            class="rounded-2xl shadow-2xl w-full h-auto"
          />
        </div>
        <div>
          <div class="inline-block bg-primary-100 text-primary-800 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            {{ caseStudy.industry }}
          </div>
          <h3 class="text-3xl font-bold text-gray-900 mb-6">{{ caseStudy.client }}</h3>

          <div class="mb-6">
            <h4 class="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <span class="material-icons text-red-600 mr-2">error_outline</span>
              The Challenge
            </h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.challenge }}</p>
          </div>

          <div>
            <h4 class="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <span class="material-icons text-green-600 mr-2">lightbulb</span>
              Our Solution
            </h4>
            <p class="text-gray-600 leading-relaxed">{{ caseStudy.solution }}</p>
          </div>
        </div>
      </div>

      <!-- Results Grid -->
      <div class="bg-gradient-to-br from-primary-800 to-primary-600 rounded-2xl p-12 text-white">
        <h4 class="text-2xl font-bold mb-8 text-center">The Results</h4>
        <div class="grid md:grid-cols-4 gap-8">
          <div *ngFor="let result of caseStudy.results" class="text-center">
            <div class="text-4xl md:text-5xl font-bold mb-2 text-primary-200">{{ result.metric }}</div>
            <div class="text-primary-100">{{ result.label }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Testimonial Section -->
<section class="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
  <div class="container mx-auto px-6">
    <div class="max-w-4xl mx-auto">
      <div class="bg-white rounded-2xl shadow-2xl p-12 relative">
        <div class="absolute top-8 left-8 text-primary-200">
          <span class="material-icons" style="font-size: 4rem;">format_quote</span>
        </div>

        <div class="relative z-10">
          <p class="text-2xl text-gray-700 leading-relaxed mb-8 italic">
            "{{ testimonial.quote }}"
          </p>

          <div class="flex items-center">
            <img
              [src]="testimonial.image"
              [alt]="testimonial.author"
              class="w-16 h-16 rounded-full mr-4 object-cover"
            />
            <div>
              <div class="font-bold text-gray-900 text-lg">{{ testimonial.author }}</div>
              <div class="text-gray-600">{{ testimonial.role }}</div>
              <div class="text-primary-600 font-semibold">{{ testimonial.company }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="py-20 bg-gradient-to-br from-primary-800 via-primary-700 to-primary-600 text-white relative overflow-hidden">
  <div class="absolute inset-0 z-0">
    <img
      src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&h=600&fit=crop"
      alt="Get Started"
      class="w-full h-full object-cover opacity-10"
    />
  </div>

  <div class="container mx-auto px-6 text-center relative z-10">
    <h2 class="text-4xl md:text-5xl font-bold mb-6">
      Ready to Automate Your Marketing?
    </h2>
    <p class="text-xl md:text-2xl mb-8 text-primary-100 max-w-3xl mx-auto">
      Let's discuss how marketing automation can transform your lead generation and customer engagement strategy.
    </p>
    <div class="flex flex-wrap gap-4 justify-center">
      <a routerLink="/contact" class="bg-white text-primary-900 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-all transform hover:scale-105 shadow-lg">
        Schedule a Consultation
      </a>
      <a routerLink="/services/performance-marketing" class="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-primary-900 transition-all">
        Explore More Services
      </a>
    </div>
  </div>

  <!-- Decorative Elements -->
  <div class="absolute bottom-0 left-0 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
  <div class="absolute top-0 right-0 w-96 h-96 bg-primary-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
</section>
